import { useState, useEffect } from "react";
import { UpdatesRepository } from "../../api/repositories/stats/updates";
import { getByLocalization, localize } from "../../common/services/localization";
import { PageContainer } from "../components/layout/page";
import { useParams } from "react-router-dom";
import { UpdateModel } from "../../api/repositories/stats/models/updates";
import { NotFoundBlock } from "../components/layout/errors";
import { isNothing } from "../../common/utils";
import { LocalizeInnerHtml, LocalizeListElement } from "../components/ui/other";
import { UpdateItemsBlock } from "../components/blocks/update/update-items";
import { UpdateGroupBlock } from "../components/blocks/update/update-group";
import { UpdateHeroesBlock } from "../components/blocks/update/update-heroes";
import { LoadingIcon, UpdateGroupTitleIcon } from "../components/ui/icons";

export const Update: React.FC = () => {
    const { url } = useParams();
    const [response, setResponse] = useState<ApiDataResponse<UpdateModel> | undefined>();

    useEffect(() => {
        const repository = new UpdatesRepository();

        if (url) {
            repository.get(url).then(v => setResponse(v));
        }
    }, [url]);

    const updateNotFoundBlock = <NotFoundBlock
        localText="update_not_found_title"
        onClickBtn={() => window.location.href = '/updates'}
        btnLocalText="update_not_found_btn"
        withImg={false}
    />

    return (
        <PageContainer
            containerClassName={``}
            headerImgPath="/v2/images/bg/dragon.png"
            child={
                <div className="max-w-[1561px] mx-auto">
                    {isNothing(url)
                        ? updateNotFoundBlock
                        : response
                            ? response?.hasErrors
                                ? updateNotFoundBlock
                                : <UpdateContainer update={response.data!} />
                            : <LoadingIcon />
                    }
                </div>
            }
        />
    );
};

const UpdateContainer: React.FC<{
    update: UpdateModel
}> = ({ update }) => {
    const containerPaddingX = 'px-[20px] lg:px-[60px]';

    const description = getByLocalization(update.ruDescription, update.engDescription);
    const hasSharedChanges = !isNothing(update.ruRows);
    const hasItems = update.items.length > 0;
    const hasHeroes = update.heroes.length > 0

    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            setTimeout(() => {
                const el = document.querySelector(hash);
                if (el) {
                    el.scrollIntoView({ behavior: 'auto' });
                }
            }, 0);
        }
    }, []);

    return <div className="flex flex-row justify-start gap-[32px] items-start text-[#ddd]">
        <div className="hidden xl:block relative w-[305px]">
            <nav
                className=" fixed w-[305px] h-fit max-h-[calc(100vh-60px)] overflow-auto
                bg-dark shadow-dark-l font-['Roboto'] gap-[12px] pb-[18px] flex flex-col items-start"
            >
                <div className={`flex bg-updates-group-title items-center  `}>
                    <UpdateGroupTitleIcon />
                    <span className="text-gray-gradient text-[18px] px-[50px] font-bold uppercase font-['Reaver']">
                        {localize("update_nav_title")}
                    </span>
                </div>
                <ul className="flex flex-col gap-[8px] px-[50px]">
                    {(
                        <li><NavLink href="#shared" text={localize('update_group_shared_title')} /></li>
                    )}
                    {hasItems && (
                        <li><NavLink href="#items" text={localize('update_group_items_title')} /></li>
                    )}
                    {hasHeroes && update.heroes.map(hero => (
                        <li key={hero.urlName}><NavLink href={`#${hero.urlName}`} text={hero.userFrendlyName} /></li>
                    ))}
                </ul>
            </nav>
        </div>

        <div id="shared" className="max-w-[1033px] xl:w-[1033px] flex flex-col text-[14px] lg:text-[16px] gap-[20px] lg:gap-[40px] bg-dark shadow-dark-l justify-center font-['Roboto'] py-[20px] lg:py-[60px]">
            <h3 className={`uppercase text-orange-dark text-center ${containerPaddingX}`}>
                {getByLocalization(update.ruName, update.engName)}
            </h3>

            {description && (
                <div className={`${containerPaddingX}`}>{description}</div>
            )}

            {hasSharedChanges && (
                <UpdateGroupBlock titleLocalName="update_group_shared_title" className={containerPaddingX}
                    child={<LocalizeInnerHtml className="mt-[18px]" ruRows={update.ruRows} engRows={update.engRows} />}
                />
            )}

            {hasItems && (
                <UpdateGroupBlock id="items" titleLocalName="update_group_items_title" className={containerPaddingX}
                    child={<UpdateItemsBlock items={update.items} />}
                />
            )}

            {hasHeroes && (
                <UpdateGroupBlock titleLocalName="update_group_heroes_title" className={containerPaddingX}
                    child={<UpdateHeroesBlock heroes={update.heroes} />}
                />
            )}
        </div>
    </div>
}

const NavLink: React.FC<{
    href: string
    text: string
    className?: string
}> = ({ href, className, text }) => {
    return (
        <a
            href={href}
            className={`hover:text-orange-500  ${className ?? ''}`}
        >
            {text}
        </a>
    );
};
